import { v4 as uuid } from 'uuid'
import * as Y from 'yjs'

import { toYType } from 'features/utils/useYjsState'

import { Layout, LayoutEditorSchema, Widget, WidgetAreas } from './types'

export function getWidgetAreaAtPath(path: string[], layout?: Layout): Widget[] | undefined {
    if (path.length < 1 || !layout) return

    if (path.length === 1) {
        const id = path[0]

        return layout.children?.[id] ?? []
    }

    const pathLen = path.length

    let currentLevelIdx = 0
    let id = path[currentLevelIdx]
    let widgetArea = id ? layout?.children?.[id] : undefined
    while (!!widgetArea && currentLevelIdx < pathLen) {
        id = path[currentLevelIdx]
        if (!id) break

        for (const widget of widgetArea) {
            if (widget.id !== id) continue

            currentLevelIdx++
            id = path[currentLevelIdx]

            widgetArea = widget.children[id]

            if (currentLevelIdx === pathLen - 1) {
                return widgetArea
            }

            break
        }

        currentLevelIdx++
    }

    return widgetArea
}

export function getWidgetAtPath(
    path: string[],
    widgetId?: string,
    layout?: Layout
): Widget | undefined {
    const widgetArea = getWidgetAreaAtPath(path, layout)
    if (!widgetArea) return

    return widgetArea.find((w) => w.id === widgetId)
}

export function getWidgetPath(path: string[], layout?: Layout): Widget[] {
    if (path.length < 2 || !layout) return []

    const widgets: Widget[] = []

    const pathLen = path.length

    let currentLevelIdx = 0
    let id = path[currentLevelIdx]
    let widgetArea = id ? layout?.children?.[id] : undefined
    while (!!widgetArea && currentLevelIdx < pathLen) {
        id = path[currentLevelIdx]
        if (!id) break

        const widget = widgetArea.find((w) => w.id === id)
        if (widget) {
            widgets.push(widget)

            // Determine the next widget area.
            currentLevelIdx++
            id = path[currentLevelIdx]
            widgetArea = widget.children[id]
        }

        currentLevelIdx++
    }

    return widgets
}

export function getYWidgetArea(path: string[], view?: Y.Map<any>): Y.Array<any> | undefined {
    if (path.length < 1 || !view) return

    if (path.length === 1) {
        const id = path[0]

        const layout = getYLayout(view)

        let children = layout.get('children')
        if (!children) {
            children = layout.set('children', new Y.Map())
        }

        let widgetArea = children.get(id)
        if (!widgetArea) {
            widgetArea = children.set(id, new Y.Array())
        }

        return widgetArea
    }

    const pathLen = path.length

    let currentLevelIdx = 0
    let id = path[currentLevelIdx]
    let widgetArea = id ? view.get('layout')?.get('children')?.get(id) : undefined
    while (!!widgetArea && currentLevelIdx < pathLen) {
        id = path[currentLevelIdx]
        if (!id) break

        for (const widget of widgetArea) {
            if (widget.get('id') !== id) continue

            let children = widget.get('children')
            if (!children) {
                children = widget.set('children', new Y.Map())
            }

            currentLevelIdx++
            id = path[currentLevelIdx]

            widgetArea = children.get(id)
            if (!widgetArea) {
                widgetArea = children.set(id, new Y.Array())
            }

            if (currentLevelIdx === pathLen - 1) {
                return widgetArea
            }

            break
        }

        currentLevelIdx++
    }

    return widgetArea
}

export function getYWidgetAtPath(
    path: string[],
    widgetId?: string,
    view?: Y.Map<any>
): { widget: Y.Map<any> | undefined; idx: number } {
    const widgetArea = getYWidgetArea(path, view)
    if (widgetArea) {
        let idx = -1
        for (const widget of widgetArea) {
            idx++

            if (widget.get('id') !== widgetId) continue

            return { widget, idx }
        }
    }

    return { widget: undefined, idx: -1 }
}

export function insertYWidgetAtPath(
    path: string[],
    schema: LayoutEditorSchema,
    widget: Partial<Widget>,
    view?: Y.Map<any>,
    targetIdx?: number
) {
    if (!view) return

    const widgetArea = getYWidgetArea(path, view)
    if (!widgetArea) return

    if (!widget.type) return
    const newWidget = createWidget(widget.type, schema, view?.toJSON() as ViewDto)
    if (!newWidget) return

    if (!!widget.id) {
        newWidget.id = widget.id
    }
    newWidget.attrs = widget.attrs || newWidget.attrs
    newWidget.children = widget.children || newWidget.children
    if (!!widget.children) {
        regenerateWidgetNestedIds(newWidget.children)
    }

    const newYWidget = toYType(newWidget)
    if (typeof targetIdx === 'number') {
        widgetArea.insert(targetIdx, [newYWidget])
    } else {
        widgetArea.push([newYWidget])
    }

    return newYWidget as Y.Map<any>
}

export function generateWidgetId() {
    return uuid()
}

export function createWidget(
    widgetType: string,
    schema: LayoutEditorSchema,
    view?: ViewDto
): Widget | undefined {
    const widgetSchema = schema.widgets[widgetType]
    if (!widgetSchema) {
        return undefined
    }

    const widgetId = generateWidgetId()

    const defaultedAttrs =
        (typeof widgetSchema.defaultAttrs === 'function'
            ? widgetSchema.defaultAttrs?.(widgetId, view)
            : widgetSchema.defaultAttrs) ?? {}

    return {
        type: widgetType,
        id: widgetId,
        children: {},
        attrs: defaultedAttrs,
    }
}

export function duplicateYWidgetAtPath(path: string[], widgetId?: string, view?: Y.Map<any>) {
    const { widget, idx } = getYWidgetAtPath(path, widgetId, view)
    if (!widget) return

    const newWidgetId = generateWidgetId()
    const newIdx = idx + 1

    const clonedWidget = widget.toJSON()
    clonedWidget.id = newWidgetId
    regenerateWidgetNestedIds(clonedWidget.children)

    const newYWidget = toYType(clonedWidget)
    if (!newYWidget) return

    const widgetArea = widget.parent as Y.Array<any> | undefined
    widgetArea?.insert(newIdx, [newYWidget])

    return newWidgetId
}

export function moveWidgetToPath(
    widgetId: string,
    initialPath: string[],
    destinationPath: string[],
    destinationIdx: number,
    data: Y.Map<any>
) {
    const { widget: existingWidget, idx: existingWidgetIdx } = getYWidgetAtPath(
        initialPath,
        widgetId,
        data
    )
    if (!existingWidget) return

    const existingWidgetArea = existingWidget.parent as Y.Array<any> | undefined
    const newWidgetArea = getYWidgetArea(destinationPath, data)
    if (!newWidgetArea) return

    // Clone the widget, since we can't use the same Yjs object in multiple places.
    const existingWidgetClone = toYType(existingWidget.toJSON())

    // Remove widget from existing widget area.
    existingWidgetArea?.delete(existingWidgetIdx)

    if (!newWidgetArea) return

    // Insert widget in new widget area.
    if (destinationIdx >= newWidgetArea.length) {
        newWidgetArea.push([existingWidgetClone])
    } else {
        newWidgetArea.insert(destinationIdx, [existingWidgetClone])
    }
}

function regenerateWidgetNestedIds(widgetAreas: WidgetAreas) {
    for (const widgetArea of Object.values(widgetAreas)) {
        for (const widget of widgetArea) {
            widget.id = generateWidgetId()

            regenerateWidgetNestedIds(widget.children)
        }
    }
}

export function getYLayout(view: Y.Map<any>): Y.Map<any> {
    let layout = view.get('layout')
    if (!layout) {
        return view.set('layout', new Y.Map())
    }

    return layout
}

export function computeRequiredFields(
    schema?: LayoutEditorSchema,
    fields?: FieldDto[],
    view?: ViewDto
) {
    const allFieldsBySid = fields?.reduce((acc, field) => {
        acc.set(field._sid, field)
        return acc
    }, new Map<string, FieldDto>())

    const requiredFields = new Set<string>()

    for (const widgetArea of Object.values(view?.layout?.children ?? {})) {
        for (const widget of widgetArea) {
            appendRequiredFieldsFromWidget(requiredFields, widget, schema, allFieldsBySid, view)
        }
    }

    return Array.from(requiredFields)
}

function appendRequiredFieldsFromWidget(
    existingFields: Set<string>,
    widget: Widget,
    schema?: LayoutEditorSchema,
    fields?: Map<string, FieldDto>,
    view?: ViewDto
) {
    const widgetSchema = schema?.widgets[widget.type]

    const defaultWidget = schema ? createWidget(widget.type, schema, view) : undefined
    const attrs = { ...defaultWidget?.attrs, ...widget.attrs }
    const requiredFields = widgetSchema?.requireFields?.(attrs, fields) ?? []
    for (const field of requiredFields) {
        existingFields.add(field)
    }

    // Visibility filters.
    const visibilityFiltersFields = getRequiredFieldsFromFilters(
        attrs.visibilityFilters ?? [],
        fields
    )
    for (const field of visibilityFiltersFields) {
        existingFields.add(field)
    }

    for (const widgetArea of Object.values(widget.children)) {
        for (const widget of widgetArea) {
            appendRequiredFieldsFromWidget(existingFields, widget, schema, fields, view)
        }
    }
}

export function getRequiredFieldsFromFilters(filters: Filter[], fields?: Map<string, FieldDto>) {
    const requiredFields = new Set<string>()

    for (const filter of filters) {
        if (!!filter.field?.api_name) {
            requiredFields.add(filter.field.api_name)
        } else if (!!filter.field_sid) {
            const field = fields?.get(filter.field_sid)
            if (!!field) {
                requiredFields.add(field.api_name)
            }
        }
    }

    return Array.from(requiredFields)
}

export function getRequiredFieldsFromActionButtons(
    actionButtons: ActionButton[],
    fields?: Map<string, FieldDto>
) {
    const requiredFields = new Set<string>()

    for (const button of actionButtons) {
        if (!button.conditions?.length) continue

        const fieldsForFilters = getRequiredFieldsFromFilters(button.conditions, fields)
        for (const field of fieldsForFilters) {
            requiredFields.add(field)
        }
    }

    return Array.from(requiredFields)
}

const CONTEXT_RECORD_FILTER_OPS = new Set([
    'isContextRecordField',
    'isNotContextRecordField',
    'containsContextRecordField',
    'doesNotContainContextRecordField',
])

export function getRequiredFieldsFromField(field: FieldDto) {
    const requiredFields = new Set<string>([field.api_name])

    if (field.type === 'lookup' || field.type === 'multi_lookup') {
        const lookupFilters = field.connection_options?.relationship_target_lookup_filters ?? []

        for (const filter of lookupFilters) {
            if (!filter.value) continue
            if (!filter.option) continue
            if (!CONTEXT_RECORD_FILTER_OPS.has(filter.option)) continue

            if (typeof filter.value === 'string') {
                requiredFields.add(filter.value)
            } else if (Array.isArray(filter.value)) {
                for (const value of filter.value) {
                    requiredFields.add(value)
                }
            }
        }
    }

    return Array.from(requiredFields)
}
